import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAllAssociations } from "./../../../../services/associationsServices";
import Modal from "./../../../../hooks/Modal";

const SaasSummaryTable = () => {
  const [saasData, setSaasData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenMessage, setIsModalOpenMessage] = useState(false);

  useEffect(() => {
    fetchSaasData();
  }, []);

  const fetchSaasData = async () => {
    setLoading(true);
    const today = new Date().toISOString().split("T")[0];
    const allAssociations = await getAllAssociations();

    const filtered = allAssociations.filter(
      (asso) =>
        asso.produit_asso === "SAAS" &&
        !!asso.apiUrl_asso &&
        asso.apiUrl_asso.startsWith("http")
    );

    const results = await Promise.all(
      filtered.map((asso) =>
        fetchDetailsFromAPI(
          {
            name: asso.nom_asso,
            logo: asso.logo_asso,
            appUrl: asso.siteweb_asso,
            apiUrl: asso.apiUrl_asso,
            dataPaths: {
              visits: "/api/visites/countVisitesByDate",
              totalUsers: "/api/users/countUsers",
              todayUsers: "/api/users/countUsersToday",
              notifications: "/api/notifications/1",
              messages: "/api/messages/allmessages/1",
            },
          },
          today
        )
      )
    );

    setSaasData(results);
    setLoading(false);
  };

  const fetchDetailsFromAPI = async (app, today) => {
    const result = {
      name: app.name,
      logo: app.logo,
      appUrl: app.appUrl,
    };

    try {
      for (const [key, path] of Object.entries(app.dataPaths)) {
        const response = await axios.get(`${app.apiUrl}${path}`);
        switch (key) {
          case "visits":
            result[key] =
              response.data.find((v) => v.date_visite === today)?.compteur || 0;
            break;
          case "notifications":
            result[key] = response.data.filter((n) =>
              n.created_at.startsWith(today)
            );
            break;
          case "messages":
            result[key] = response.data.filter((m) =>
              m.created_at.startsWith(today)
            );
            break;
          default:
            if (key === "totalUsers") {
              result[key] = response.data.total || 0;
            } else {
              result[key] = response.data.count || 0;
            }
        }
      }
    } catch (error) {
      console.error(`Erreur pour ${app.name}:`, error.message);
      result.error = "Erreur";
    }

    return result;
  };

  const handleViewNotifications = (notifications) => {
    setSelectedNotifications(notifications);
    setIsModalOpen(true);
  };

  const handleViewMessages = (messages) => {
    setSelectedMessages(messages);
    setIsModalOpenMessage(true);
  };

  const formatDateTime = (dateTime) => {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateTime));
  };

  const totalStats = saasData.reduce(
    (acc, app) => {
      acc.visits += Number(app.visits) || 0;
      acc.totalUsers += Number(app.totalUsers) || 0;
      acc.todayUsers += Number(app.todayUsers) || 0;
      acc.notifications += Array.isArray(app.notifications)
        ? app.notifications.length
        : 0;
      acc.messages += Array.isArray(app.messages)
        ? app.messages.filter((m) => !m.is_copy).length
        : 0;
      return acc;
    },
    {
      visits: 0,
      totalUsers: 0,
      todayUsers: 0,
      notifications: 0,
      messages: 0,
    }
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">
        Statistiques des produits SAAS
      </h2>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-6">Produit</th>
                <th className="py-3 px-6">Visites</th>
                <th className="py-3 px-6">Utilisateurs</th>
                <th className="py-3 px-6">Notifications</th>
                <th className="py-3 px-6">Messages</th>
              </tr>
            </thead>
            <tbody>
              {saasData.map((app, index) => (
                <tr
                  key={index}
                  className={`border-b ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="py-3 px-6 flex items-center gap-2">
                    <img
                      src={app.logo}
                      alt={app.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <a
                      href={app.appUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {app.name}
                    </a>
                  </td>
                  <td className="py-3 px-6">{app.visits || 0}</td>
                  <td className="py-3 px-6">
                    {app.totalUsers}
                    {app.todayUsers > 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded text-white text-xs bg-red-500">
                        +{app.todayUsers}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-6">
                    <button
                      className={`px-3 py-1 rounded text-white text-sm ${
                        app.notifications?.length > 0
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-400"
                      }`}
                      disabled={app.notifications?.length === 0}
                      onClick={() => handleViewNotifications(app.notifications)}
                    >
                      {app.notifications?.length || 0}
                    </button>
                  </td>
                  <td className="py-3 px-6">
                    <button
                      className={`px-3 py-1 rounded text-white text-sm ${
                        app.messages?.length > 0
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-400"
                      }`}
                      disabled={app.messages?.length === 0}
                      onClick={() => handleViewMessages(app.messages)}
                    >
                      {app.messages?.length || 0}
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-200 font-bold">
                <td className="py-3 px-6">Total</td>
                <td className="py-3 px-6">{totalStats.visits}</td>
                <td className="py-3 px-6">
                  {totalStats.totalUsers}
                  {totalStats.todayUsers > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded text-white text-xs bg-red-500">
                      +{totalStats.todayUsers}
                    </span>
                  )}
                </td>
                <td className="py-3 px-6">{totalStats.notifications}</td>
                <td className="py-3 px-6">{totalStats.messages}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Modale Notifications */}
      {isModalOpen && (
        <Modal
          onClose={() => setIsModalOpen(false)}
          title="Notifications du jour"
        >
          <table className="min-w-full bg-gray-200 text-gray-800">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Message</th>
                <th className="py-2 px-4 border-b">Date</th>
              </tr>
            </thead>
            <tbody>
              {selectedNotifications.map((n, i) => (
                <tr key={i}>
                  <td className="py-2 px-4 border-b">{n.message}</td>
                  <td className="py-2 px-4 border-b">
                    {formatDateTime(n.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}

      {/* Modale Messages */}
      {isModalOpenMessage && (
        <Modal
          onClose={() => setIsModalOpenMessage(false)}
          title="Messages du jour"
        >
          <table className="min-w-full bg-gray-200 text-gray-800">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Message</th>
                <th className="py-2 px-4 border-b">Date</th>
              </tr>
            </thead>
            <tbody>
              {selectedMessages.map((m, i) => (
                <tr key={i}>
                  <td
                    className="py-2 px-4 border-b"
                    dangerouslySetInnerHTML={{ __html: m.content }}
                  ></td>
                  <td className="py-2 px-4 border-b">
                    {formatDateTime(m.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
};

export default SaasSummaryTable;
